import React, { useCallback, useEffect, useState } from "react";
import { Alert, Grid, Snackbar, TextField, Typography } from "@mui/material";
import { useHistory, useLocation, Link } from "react-router-dom";
import profileTemplate from "../data/profileTemplate.json";
import { uploadFile } from "../utilities/updateS3";
import { nameToSlug } from "./PhotoUpload";
import { sportmapReverse } from "../utilities/sportmap";
import ProfileForm from "./ProfileForm";
import PasswordModal from "./PasswordModal";

const initialBioFromTemplate = () => {
  const bio = {};
  profileTemplate.forEach((q) => { bio[q.key] = q.value; });
  return bio;
};

const AddAthlete = () => {
  const location = useLocation();
  const history = useHistory();
  const { suggestedId, isAdmin } = location.state ?? {};

  const [id, setId] = useState(suggestedId ?? "");
  const [name, setName] = useState("");
  const [sport, setSport] = useState("");
  const [secondSport, setSecondSport] = useState("");
  const [active, setActive] = useState("yes");
  const [meta, setMeta] = useState("no");
  const [published, setPublished] = useState("no");
  const [listHistory, setListHistory] = useState(true);
  const [bio, setBio] = useState(initialBioFromTemplate);
  const [social, setSocial] = useState({});
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [password, setPassword] = useState("");
  const [showAlert, setShowAlert] = useState(false);
  const [alertStatus, setAlertStatus] = useState("");
  const [alertText, setAlertText] = useState("");
  const [createdAthlete, setCreatedAthlete] = useState(null);
  const [existingAthletes, setExistingAthletes] = useState([]);

  useEffect(() => {
    if (!isAdmin) history.push("/athlete-portal");
  }, [isAdmin, history]);

  useEffect(() => {
    fetch("https://plantbuilt.s3.us-west-2.amazonaws.com/athletes.json", { cache: "no-cache" })
      .then((r) => r.json())
      .then(setExistingAthletes)
      .catch(console.error);
  }, []);

  const idConflict = existingAthletes.find((a) => String(a.id) === String(id).trim());

  const handleSelection = useCallback((e) => {
    const { name: n, value } = e.target;
    if (n === "sport") setSport(value);
    if (n === "secondSport") setSecondSport(value);
    if (n === "active") setActive(value);
    if (n === "listHistory") setListHistory((prev) => !prev);
    if (n === "meta") setMeta(value);
    if (n === "published") setPublished(value);
  }, []);

  const buildBio = useCallback(() => {
    const orderedBio = [];
    if (bio["Current City"]) orderedBio.push({ key: "Current City", value: bio["Current City"] });
    if (bio["Current Occupation"]) orderedBio.push({ key: "Current Occupation", value: bio["Current Occupation"] });
    if (bio["Hometown"]) orderedBio.push({ key: "Hometown", value: bio["Hometown"] });
    if (bio["Birth Year"]) orderedBio.push({ key: "Birth Year", value: bio["Birth Year"] });
    if (bio["Division"]) orderedBio.push({ key: "Division", value: bio["Division"] });
    if (bio["Vegan Since"]) orderedBio.push({ key: "Vegan Since", value: bio["Vegan Since"] });
    if (bio["Why you became a vegan"]) orderedBio.push({ key: "Why you became a vegan", value: bio["Why you became a vegan"] });
    if (bio["Favorite foods/meals"]) orderedBio.push({ key: "Favorite foods/meals", value: bio["Favorite foods/meals"] });
    if (bio["Favorite exercises"]) orderedBio.push({ key: "Favorite exercises", value: bio["Favorite exercises"] });
    if (listHistory && bio["Competition Highlights"]?.length) {
      orderedBio.push({ key: "Competition Highlights", value: bio["Competition Highlights"] });
    }
    return orderedBio;
  }, [bio, listHistory]);

  const submitProfile = useCallback(async () => {
    if (!name || !sport || !id) {
      setAlertStatus("error");
      setShowAlert(true);
      setAlertText("Name, Sport, and ID are required before submitting.");
      return;
    }
    if (idConflict) {
      setAlertStatus("error");
      setShowAlert(true);
      setAlertText(`ID ${id} is already assigned to ${idConflict.name}.`);
      return;
    }
    const profile = {
      name,
      id: Number(id),
      img: `https://plantbuilt.s3.us-west-2.amazonaws.com/profile-photos/plantbuilt-${nameToSlug(name)}.png`,
      meta: `https://plantbuilt.s3.us-west-2.amazonaws.com/profile-photos/plantbuilt-${nameToSlug(name)}-meta.png`,
      active: active === "yes",
      published: published === "yes",
      sport: sportmapReverse[sport],
      secondSport: sportmapReverse[secondSport] ?? null,
      bio: buildBio(),
      social: [],
    };
    for (let link in social) {
      if (social[link]) profile.social.push({ key: link, value: social[link] });
    }
    try {
      const response = await uploadFile(password, id, JSON.stringify(profile));
      if (response.status === 200) {
        setCreatedAthlete({ name, id });
      } else {
        throw new Error("Upload failed");
      }
    } catch {
      setAlertStatus("error");
      setShowAlert(true);
      setAlertText("There was an issue creating the profile. Please try again.");
    }
  }, [active, buildBio, id, idConflict, name, password, published, social, sport, secondSport]);

  return (
    <Grid container>
      {showAlert && (
        <Snackbar open={showAlert} autoHideDuration={6000} onClose={() => setShowAlert(false)}>
          <Alert onClose={() => setShowAlert(false)} severity={alertStatus} variant="filled">
            {alertText}
          </Alert>
        </Snackbar>
      )}
      {createdAthlete && (
        <Grid item xs={12} style={{ padding: "8px" }}>
          <Alert severity="success" variant="filled">
            <Typography variant="body1">
              <strong>{createdAthlete.name}</strong> (ID: {createdAthlete.id}) created successfully.
            </Typography>
            <Link
              to={{
                pathname: "/athlete-portal/upload-photo",
                state: { userProfile: { name: createdAthlete.name, id: createdAthlete.id }, isAdmin: true },
              }}
              style={{ color: "white", display: "block", marginTop: "8px" }}
            >
              Upload photo for this athlete →
            </Link>
            <Link
              to="/athlete-portal"
              style={{ color: "white", display: "block", marginTop: "4px" }}
            >
              Back to portal →
            </Link>
          </Alert>
        </Grid>
      )}
      <Grid item xs={12} style={{ padding: "8px" }}>
        <Typography variant="h6">Add New Athlete</Typography>
      </Grid>
      <Grid item xs={12} style={{ padding: "8px" }}>
        <TextField
          required
          label="Athlete ID"
          value={id}
          onChange={(e) => setId(e.target.value)}
          error={!!idConflict}
          helperText={
            idConflict
              ? `ID already assigned to ${idConflict.name}`
              : "Unique numeric ID — verify against the athlete register before saving"
          }
          style={{ width: "240px" }}
        />
      </Grid>
      <ProfileForm
        active={active}
        bio={bio}
        handleSelection={handleSelection}
        listHistory={listHistory}
        meta={meta}
        name={name}
        previousCompHistory={null}
        published={published}
        secondSport={secondSport}
        setBio={setBio}
        setName={setName}
        setSecondSport={setSecondSport}
        setShowPasswordModal={setShowPasswordModal}
        setSocial={setSocial}
        social={social}
        sport={sport}
      />
      <PasswordModal
        showPasswordModal={showPasswordModal}
        hideModal={() => setShowPasswordModal(false)}
        password={password}
        setPassword={setPassword}
        submitProfile={submitProfile}
      />
    </Grid>
  );
};

export default AddAthlete;
